//chart-page/charts/WatchPricesChart/calculations.ts
import { DataPoint, Statistics, WatchData, DataSource } from '../types';

interface EnhancedDataPoint {
  date: string;
  price: number;
  formattedDate: string;
  ma50: number | null;
  volume?: number;
  raw_price?: number | null;
  hma20?: number | null;
  median50?: number | null;
  trendMA?: number | null;
  vwap?: number | null;
  keltner_middle?: number | null;
  keltner_upper?: number | null;
  keltner_lower?: number | null;
  regression_value?: number | null;
  regression_upper?: number | null;
  regression_lower?: number | null;
  dmi_adx?: number | null;
  dmi_plus?: number | null;
  dmi_minus?: number | null;
  fairValue?: number | null;
  grailzee_price?: number | null;
  grailzee_raw_price?: number | null;
}

export const calculateHMA = (data: EnhancedDataPoint[], period: number) => {
  if (!data || data.length === 0) return [];
  if (data.length === 1) return [data[0].price];

  const sqrt = Math.floor(Math.sqrt(period));

  const wma = (prices: number[], len: number) => {
    const result = new Array(prices.length);
    for (let i = 0; i < prices.length; i++) {
      if (i < len - 1) {
        result[i] = prices[i]; // Use actual price instead of null
        continue;
      }
      const slice = prices.slice(i - len + 1, i + 1);
      const denominator = (len * (len + 1)) / 2;
      result[i] =
        slice.reduce((sum, price, idx) => sum + price * (idx + 1), 0) /
        denominator;
    }
    return result;
  };

  const prices = data.map((d) => d.price);
  const halfWMA = wma(prices, Math.floor(period / 2));
  const fullWMA = wma(prices, period);

  const combined = halfWMA.map((half, i) => 2 * half - fullWMA[i]);
  return wma(combined, sqrt);
};

export const calculateKeltnerChannels = (
  data: EnhancedDataPoint[],
  emaPeriod: number = 20,
  atrPeriod: number = 10,
  multiplier: number = 2
) => {
  const ema = calculateEMA(data, emaPeriod);

  const trueRanges = data.map((point, i) => {
    if (i === 0) return point.price * 0.02;
    const previousClose = data[i - 1].price;
    const high = Math.max(point.price, previousClose);
    const low = Math.min(point.price, previousClose);
    return high - low;
  });

  const atr = trueRanges.map((_, idx, arr) => {
    if (idx < atrPeriod - 1) return arr[idx];
    const slice = arr.slice(idx - atrPeriod + 1, idx + 1);
    return slice.reduce((sum, tr) => sum + tr, 0) / atrPeriod;
  });

  return data.map((_, i) => ({
    middle: ema[i],
    upper: ema[i] + multiplier * atr[i],
    lower: ema[i] - multiplier * atr[i],
  }));
};

export const calculateRegression = (
  data: EnhancedDataPoint[],
  period: number = 20
) => {
  return data.map((_, idx, arr) => {
    if (idx < period - 1) return null;

    const slice = arr.slice(idx - period + 1, idx + 1);
    const x = Array.from({ length: period }, (_, i) => i);
    const y = slice.map((point) => point.price);

    const xMean = x.reduce((a, b) => a + b, 0) / period;
    const yMean = y.reduce((a, b) => a + b, 0) / period;

    const slope =
      x.reduce((sum, xi, i) => sum + (xi - xMean) * (y[i] - yMean), 0) /
      x.reduce((sum, xi) => sum + Math.pow(xi - xMean, 2), 0);

    const intercept = yMean - slope * xMean;
    const regressionValue = slope * (period - 1) + intercept;

    const predicted = x.map((xi) => slope * xi + intercept);
    const residuals = y.map((yi, i) => yi - predicted[i]);
    const standardError = Math.sqrt(
      residuals.reduce((sum, r) => sum + r * r, 0) / (period - 2)
    );

    return {
      value: regressionValue,
      upper: regressionValue + 2 * standardError,
      lower: regressionValue - 2 * standardError,
    };
  });
};

export const calculateDMI = (
  data: EnhancedDataPoint[],
  period: number = 14
) => {
  const tr = new Array(data.length).fill(0);
  const dmPlus = new Array(data.length).fill(0);
  const dmMinus = new Array(data.length).fill(0);

  for (let i = 1; i < data.length; i++) {
    const high = data[i].price * 1.001;
    const low = data[i].price * 0.999;
    const prevHigh = data[i - 1].price * 1.001;
    const prevLow = data[i - 1].price * 0.999;
    const prevClose = data[i - 1].price;

    tr[i] = Math.max(
      high - low,
      Math.abs(high - prevClose),
      Math.abs(low - prevClose)
    );

    dmPlus[i] =
      high - prevHigh > prevLow - low ? Math.max(high - prevHigh, 0) : 0;
    dmMinus[i] =
      prevLow - low > high - prevHigh ? Math.max(prevLow - low, 0) : 0;
  }

  const trSum = tr.reduce((sum, value, i, arr) => {
    if (i < period) return sum + value;
    return sum - arr[i - period] / period + value / period;
  }, 0);

  const plusDMSum = dmPlus.reduce((sum, value, i, arr) => {
    if (i < period) return sum + value;
    return sum - arr[i - period] / period + value / period;
  }, 0);

  const minusDMSum = dmMinus.reduce((sum, value, i, arr) => {
    if (i < period) return sum + value;
    return sum - arr[i - period] / period + value / period;
  }, 0);

  return data.map((_, i) => {
    if (i < period) return null;

    const diPlus = (plusDMSum / trSum) * 100;
    const diMinus = (minusDMSum / trSum) * 100;
    const dx = (Math.abs(diPlus - diMinus) / (diPlus + diMinus)) * 100;

    return {
      adx: dx,
      diPlus,
      diMinus,
    };
  });
};

export const calculateMA = (data: EnhancedDataPoint[], period: number) => {
  return data.map((_, index) => {
    if (index < period - 1) return null;
    const slice = data.slice(index - period + 1, index + 1);
    return slice.reduce((sum, point) => sum + point.price, 0) / period;
  });
};

export const calculateEMA = (data: EnhancedDataPoint[], period: number) => {
  // Handle empty data or invalid first point
  if (
    !data ||
    data.length === 0 ||
    !data[0] ||
    typeof data[0].price !== 'number'
  ) {
    return [];
  }

  const k = 2 / (period + 1);
  let ema = data[0].price;

  return data.map((point) => {
    if (!point || typeof point.price !== 'number') {
      return ema; // Return last valid EMA if current point is invalid
    }
    ema = point.price * k + ema * (1 - k);
    return ema;
  });
};

export const calculateBollingerBands = (
  data: EnhancedDataPoint[],
  period: number = 20,
  stdDev: number = 2
) => {
  const sma = data.map((_, idx, arr) => {
    if (idx < period - 1) return null;
    const slice = arr.slice(idx - period + 1, idx + 1);
    return slice.reduce((sum, point) => sum + point.price, 0) / period;
  });

  const bands = sma.map((mean, idx) => {
    if (idx < period - 1 || mean === null)
      return { upper: null, lower: null, middle: null };
    const slice = data.slice(idx - period + 1, idx + 1);
    const std = Math.sqrt(
      slice.reduce((sum, point) => sum + Math.pow(point.price - mean, 2), 0) /
        period
    );
    return {
      upper: mean + stdDev * std,
      lower: mean - stdDev * std,
      middle: mean,
    };
  });

  return bands;
};

export const calculateMedianPrice = (
  data: EnhancedDataPoint[],
  period: number
) => {
  return data.map((_, idx, arr) => {
    if (idx < period - 1) return null;
    const slice = arr.slice(idx - period + 1, idx + 1);
    const sortedPrices = slice.map((p) => p.price).sort((a, b) => a - b);
    return sortedPrices[Math.floor(sortedPrices.length / 2)];
  });
};

export const calculateTrendMA = (data: EnhancedDataPoint[], period: number) => {
  const sma = calculateMA(data, period);
  const momentum = data.map((_, idx, arr) => {
    if (idx < period) return null;
    const priceChange = arr[idx].price - arr[idx - period].price;
    return priceChange / period;
  });

  return sma.map((avg, idx) => {
    if (avg === null || momentum[idx] === null) return null;
    return avg + momentum[idx] * period * 0.5;
  });
};

export const calculateVWAP = (
  data: EnhancedDataPoint[],
  period: number = 20
) => {
  return data.map((_, idx, arr) => {
    if (idx < period - 1) return null;
    const slice = arr.slice(idx - period + 1, idx + 1);
    const sumPriceVolume = slice.reduce(
      (sum, point) => sum + point.price * (point.volume || 1),
      0
    );
    const sumVolume = slice.reduce(
      (sum, point) => sum + (point.volume || 1),
      0
    );
    return sumPriceVolume / sumVolume;
  });
};

export const calculateStatistics = (data: EnhancedDataPoint[]): Statistics => {
  const prices = data.map((d) => d.price);
  const sortedPrices = [...prices].sort((a, b) => a - b);

  const returns = prices
    .slice(1)
    .map((price, i) => (price - prices[i]) / prices[i]);
  const volatility =
    Math.sqrt(
      returns.reduce((acc, ret) => acc + ret * ret, 0) / returns.length
    ) *
    Math.sqrt(252) *
    100;

  const recentPrices = prices.slice(-30);
  const xValues = Array.from({ length: recentPrices.length }, (_, i) => i);
  const xMean = xValues.reduce((a, b) => a + b, 0) / xValues.length;
  const yMean = recentPrices.reduce((a, b) => a + b, 0) / recentPrices.length;

  const slope =
    xValues.reduce(
      (acc, x, i) => acc + (x - xMean) * (recentPrices[i] - yMean),
      0
    ) / xValues.reduce((acc, x) => acc + Math.pow(x - xMean, 2), 0);

  return {
    totalListings: data.length,
    priceRange: {
      min: sortedPrices[0],
      max: sortedPrices[sortedPrices.length - 1],
    },
    averagePrice: prices.reduce((a, b) => a + b, 0) / prices.length,
    medianPrice: sortedPrices[Math.floor(sortedPrices.length / 2)],
    volatility,
    currentTrend: slope > 0.02 ? 'up' : slope < -0.02 ? 'down' : 'neutral',
    percentageChange:
      ((prices[prices.length - 1] - prices[0]) / prices[0]) * 100,
  };
};

const aggregateDataPoints = (
  data: EnhancedDataPoint[],
  threshold: number = 2
) => {
  if (!data.length) return [];

  const result: EnhancedDataPoint[] = [];
  const dateMap = new Map<string, EnhancedDataPoint[]>();

  data.forEach((point) => {
    const date = point.date;
    if (!dateMap.has(date)) {
      dateMap.set(date, []);
    }
    dateMap.get(date)!.push(point);
  });

  Array.from(dateMap.entries())
    .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime())
    .forEach(([date, points]) => {
      const weightedPrice =
        points.reduce((acc, point, idx) => {
          const weight = Math.exp(idx / points.length);
          return acc + point.price * weight;
        }, 0) /
        points.reduce((acc, _, idx) => acc + Math.exp(idx / points.length), 0);

      result.push({
        ...points[points.length - 1],
        price: weightedPrice,
        ma50: null,
      });
    });

  return result;
};

export const processWatchData = (
  data: EnhancedDataPoint[]
): EnhancedDataPoint[] => {
  if (!data || data.length === 0) return [];

  let enrichedData = [...data];

  try {
    const ema20 = calculateEMA(enrichedData, 20);
    const hma20 = calculateHMA(enrichedData, 20);
    const median50 = calculateMedianPrice(enrichedData, 50);
    const trendMA = calculateTrendMA(enrichedData, 30);
    const vwap20 = calculateVWAP(enrichedData, 20);
    const keltner = calculateKeltnerChannels(enrichedData);
    const regression = calculateRegression(enrichedData);
    const dmi = calculateDMI(enrichedData);

    enrichedData = enrichedData.map((point, idx) => ({
      ...point,
      price: ema20[idx] || point.price,
      raw_price: point.price,
      hma20: hma20[idx] || null,
      median50: median50[idx] || null,
      trendMA: trendMA[idx] || null,
      vwap: vwap20[idx] || null,
      keltner_middle: keltner[idx]?.middle ?? null,
      keltner_upper: keltner[idx]?.upper ?? null,
      keltner_lower: keltner[idx]?.lower ?? null,
      regression_value: regression[idx]?.value ?? null,
      regression_upper: regression[idx]?.upper ?? null,
      regression_lower: regression[idx]?.lower ?? null,
      dmi_adx: dmi[idx]?.adx ?? null,
      dmi_plus: dmi[idx]?.diPlus ?? null,
      dmi_minus: dmi[idx]?.diMinus ?? null,
      fairValue: calculateFairValue(
        point,
        idx,
        ema20,
        vwap20,
        keltner,
        regression,
        median50
      ),
    }));

    return enrichedData;
  } catch (error) {
    console.error('Error processing watch data:', error);
    return data; // Return original data if processing fails
  }
};

const calculateFairValue = (
  point: EnhancedDataPoint,
  idx: number,
  ema20: (number | null)[],
  vwap20: (number | null)[],
  keltner: { middle?: number | null }[],
  regression: ({ value: number; upper: number; lower: number } | null)[],
  median50: (number | null)[]
): number => {
  const values = [
    ema20[idx],
    vwap20[idx],
    keltner[idx]?.middle,
    regression[idx]?.value ?? null,
    median50[idx],
  ].filter((v): v is number => typeof v === 'number' && v !== null);

  if (values.length === 0) return point.price;

  return values.reduce((sum, val) => sum + val, 0) / values.length;
};

export const processChartData = (
  watches: Map<string, WatchData>,
  selectedSources: Set<DataSource>,
  visibilityMap: Record<string, boolean>
) => {
  // ... chart data processing implementation
};
